import type {
  PartnerApplicationRequest,
  PartnerAssetsView,
  PartnerHomeRange,
  PartnerHomeResponse,
  PartnerJoinInfo,
  PartnerLoginRequest,
  PartnerMe,
  PartnerMembershipResponse,
  PartnerMyAssetResponse,
  PartnerMyAssetsView,
  PartnerPayoutMethodRequest,
  PartnerPayoutsView,
  PartnerProfileUpdateRequest,
  PartnerReferralsView,
  PartnerSignAgreementRequest,
  PartnerSignupRequest,
  PartnerTaxRequest,
} from "../../shared/types";

/**
 * The portal's typed client for the real partner API (node P1). Every call is a
 * real request against `/api/partner/*` with the first-party session cookie. No
 * fabricated session, no fabricated partner: the API is the only source of truth.
 */
export class PartnerApiError extends Error {
  /** HTTP status, or 0 for a transport-level failure (offline, DNS, CORS). */
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "PartnerApiError";
    this.status = status;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function errorMessage(data: unknown, status: number): string {
  return (
    (data && typeof data === "object" && "error" in data && typeof data.error === "string"
      ? data.error
      : null) ?? `Something went wrong (${status}).`
  );
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/partner${path}`, {
      method,
      headers: { "content-type": "application/json", accept: "application/json" },
      credentials: "same-origin",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new PartnerApiError("Network error. Check your connection and try again.", 0);
  }
  const text = await res.text();
  const data: unknown = text ? safeJson(text) : null;
  if (!res.ok) throw new PartnerApiError(errorMessage(data, res.status), res.status);
  return data as T;
}

export const partnerApi = {
  /** The signed-in partner + their programs, or null when no session (401). */
  async me(): Promise<PartnerMe | null> {
    try {
      return await request<PartnerMe>("GET", "/me");
    } catch (err) {
      if (err instanceof PartnerApiError && (err.status === 401 || err.status === 0)) return null;
      throw err;
    }
  },

  signup(body: PartnerSignupRequest): Promise<PartnerMe> {
    return request<PartnerMe>("POST", "/auth/signup", body);
  },

  login(body: PartnerLoginRequest): Promise<PartnerMe> {
    return request<PartnerMe>("POST", "/auth/login", body);
  },

  async logout(): Promise<void> {
    await request<{ ok: boolean }>("POST", "/auth/logout");
  },

  joinInfo(slug: string): Promise<PartnerJoinInfo> {
    return request<PartnerJoinInfo>("GET", `/join/${encodeURIComponent(slug)}`);
  },

  saveApplication(body: PartnerApplicationRequest): Promise<PartnerMembershipResponse> {
    return request<PartnerMembershipResponse>("PUT", "/application", body);
  },

  signAgreement(body: PartnerSignAgreementRequest): Promise<PartnerMembershipResponse> {
    return request<PartnerMembershipResponse>("POST", "/agreement/sign", body);
  },

  submitTax(body: PartnerTaxRequest): Promise<PartnerMembershipResponse> {
    return request<PartnerMembershipResponse>("PUT", "/tax", body);
  },

  savePayoutMethod(body: PartnerPayoutMethodRequest): Promise<PartnerMembershipResponse> {
    return request<PartnerMembershipResponse>("PUT", "/payout-method", body);
  },

  /** The signed-in partner's own home for one program (node P2). */
  home(programId: string, range: PartnerHomeRange = "30d"): Promise<PartnerHomeResponse> {
    const q = new URLSearchParams({ programId, range });
    return request<PartnerHomeResponse>("GET", `/home?${q.toString()}`);
  },

  /** The signed-in partner's own referrals + lifetime KPIs for one program (node P3). */
  referrals(programId: string): Promise<PartnerReferralsView> {
    const q = new URLSearchParams({ programId });
    return request<PartnerReferralsView>("GET", `/referrals?${q.toString()}`);
  },

  /** The owner-shared asset library the partner may see for one program (node P4). */
  assets(programId: string): Promise<PartnerAssetsView> {
    const q = new URLSearchParams({ programId });
    return request<PartnerAssetsView>("GET", `/assets?${q.toString()}`);
  },

  /**
   * The signed-in partner's own payout position for one program (node P5): what is
   * owed, clearing, and paid. READ only, there is no pay action; the owner pays
   * directly and Flashadz records it.
   */
  payouts(programId: string): Promise<PartnerPayoutsView> {
    const q = new URLSearchParams({ programId });
    return request<PartnerPayoutsView>("GET", `/payouts?${q.toString()}`);
  },

  /**
   * The partner-authorized download URL for one asset in a program. This is the
   * real streaming endpoint (R2 has no presigned URL through a Worker binding), so
   * the URL IS the download: the server resolves the product from the session and
   * access-checks the asset before streaming. Used by an <a> download and the
   * preview fetch; the same-origin session cookie authorizes it.
   */
  assetDownloadUrl(assetId: string, programId: string): string {
    const q = new URLSearchParams({ programId });
    return `/api/partner/assets/${encodeURIComponent(assetId)}/download?${q.toString()}`;
  },

  /** The signed-in partner's OWN uploaded assets for one program (node P4). */
  myAssets(programId: string): Promise<PartnerMyAssetsView> {
    const q = new URLSearchParams({ programId });
    return request<PartnerMyAssetsView>("GET", `/my-assets?${q.toString()}`);
  },

  /**
   * Upload one of the partner's own assets (real; stored in R2). The program is
   * sent in the form so the server scopes the asset to that enrollment; the
   * owning partner id always comes from the session, never the client. Uses XHR so
   * the caller can render real upload progress. Returns the created asset.
   */
  uploadMyAsset(
    programId: string,
    file: File,
    name: string,
    onProgress?: (pct: number) => void,
  ): Promise<PartnerMyAssetResponse> {
    return new Promise((resolve, reject) => {
      const form = new FormData();
      form.append("programId", programId);
      form.append("name", name);
      form.append("file", file);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", "/api/partner/my-assets");
      xhr.withCredentials = true;
      if (onProgress && xhr.upload) {
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        });
      }
      xhr.addEventListener("load", () => {
        const data: unknown = xhr.responseText ? safeJson(xhr.responseText) : null;
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data as PartnerMyAssetResponse);
        } else {
          reject(new PartnerApiError(errorMessage(data, xhr.status), xhr.status));
        }
      });
      xhr.addEventListener("error", () =>
        reject(new PartnerApiError("Network error. Check your connection and try again.", 0)),
      );
      xhr.send(form);
    });
  },

  /**
   * The partner-authorized download URL for one of the partner's own assets.
   * Ownership resolves from the session, so no program id is needed; the
   * same-origin session cookie authorizes the stream.
   */
  myAssetDownloadUrl(assetId: string): string {
    return `/api/partner/my-assets/${encodeURIComponent(assetId)}/download`;
  },

  /** Delete one of the partner's own assets (removes the row + its R2 object). */
  async deleteMyAsset(assetId: string): Promise<void> {
    await request<{ ok: boolean }>("DELETE", `/my-assets/${encodeURIComponent(assetId)}`);
  },

  /**
   * Update the signed-in partner's own profile (node P6): first/last name + phone,
   * and, optionally, the password (current verified server-side, re-hashed).
   * Returns the refreshed session so the shell reflects the change immediately.
   */
  updateProfile(body: PartnerProfileUpdateRequest): Promise<PartnerMe> {
    return request<PartnerMe>("PATCH", "/profile", body);
  },

  /** Upload a new profile photo (real; stored in R2). Returns the refreshed session. */
  async uploadAvatar(blob: Blob): Promise<PartnerMe> {
    let res: Response;
    try {
      res = await fetch("/api/partner/avatar", {
        method: "PUT",
        headers: { "content-type": blob.type || "image/jpeg" },
        credentials: "same-origin",
        body: blob,
      });
    } catch {
      throw new PartnerApiError("Network error. Check your connection and try again.", 0);
    }
    const text = await res.text();
    const data: unknown = text ? safeJson(text) : null;
    if (!res.ok) throw new PartnerApiError(errorMessage(data, res.status), res.status);
    return data as PartnerMe;
  },

  /** Remove the profile photo. Returns the refreshed session. */
  removeAvatar(): Promise<PartnerMe> {
    return request<PartnerMe>("DELETE", "/avatar");
  },
};
